import { ICustomerAddress } from "@/api_framework/api_modals/Addresses";
import { IBookingCountListResponseData } from "@/api_framework/api_modals/Bookings";
import { IEMRListResponseData, ILABListResponseData } from "@/api_framework/api_modals/EMRs";
import { IIvoiceListResponseData } from "@/api_framework/api_modals/Invoce";
import { ICustomerPetListPet } from "@/api_framework/api_modals/Pets";
import {
  ICustomerClinicmanagerPhoneResponseData,
  ICustomerDetailsCustomerProfile,
  ICustomerUserProfileResponseData,
  IExotelInformationData,
  ISearchCustomerResponseData,
} from "@/api_framework/api_modals/Users";
import { ICustomerDetailsHeader } from "@/app/components/routes/auth_routes/CustomerProfile/customer_profile_interface";
import VMSConstants from "@/constants";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { IRouteReducerSliceStateInit } from "../slice_interface";
import {
  IRemindersResponseData,
  IVaccinationHistoryResponseData,
  IVaccinationInputResponseData,
  IVaccinationReminderResponseData,
  IVaccinationSummaryResponseData,
} from "@/api_framework/api_modals/Vaccination";
import { IVaccinationOCRResponseData } from "@/app/hooks/api_hooks/Vaccination/useVaccinationOCRDataSubmissionAPI";

export type CustomerProfileStateKeys =
  | "missingDetailsRedirect"
  | "searchCustomerQuery"
  | "customerId"
  | "petId"
  | "customerDetails"
  | "customerAddresses"
  | "customerPets"
  | "petDetails"
  | "searchCustomerToggleCode"
  | "headers"
  | "customerEMRDetails"
  | "customerLABDetails"
  | "userProfile"
  | "customerPrimaryAddress"
  | "customerProfileView"
  | "invoice"
  | "clinicManagersMobile"
  | "bookingCountList"
  | "appointmentFilter";

export type SearchCustomerToggleCode = "0" | "1";

export type CustomerProfileViewType = "GRID" | "DEFAULT";

export type AppointmentFilterType = "ALL" | "TODAY" | "UPCOMING";

export interface ICustomerProfileState {
  missingDetailsRedirect: boolean;
  customerProfileView: CustomerProfileViewType;
  searchCustomerQuery: string | null;
  customerSearchResults: ISearchCustomerResponseData[];
  customerId: string | null;
  customerMembershipStatus: boolean;
  petId: string | null;
  customerDetails: ICustomerDetailsCustomerProfile;
  customerAddresses: ICustomerAddress[];
  customerPrimaryAddress: ICustomerAddress;
  customerPets: ICustomerPetListPet[];
  petDetails: ICustomerPetListPet;
  searchCustomerToggleCode: SearchCustomerToggleCode | null;
  headers: ICustomerDetailsHeader[] | null;
  customerEMRDetails: IEMRListResponseData[] | null;
  customerLABDetails: ILABListResponseData[] | null;
  userProfile: ICustomerUserProfileResponseData | null;
  invoice: IIvoiceListResponseData[] | null;
  clinicManagersMobile: ICustomerClinicmanagerPhoneResponseData | null;
  bookingCountList: IBookingCountListResponseData | null;
  appointmentFilter: AppointmentFilterType;
  customerMembershipDetails: string;
  vaccinationData: IVaccinationHistoryResponseData[] | null;
  vaccinationInputData: IVaccinationInputResponseData[] | null;
  vaccinationSummaryData: IVaccinationSummaryResponseData[] | null;
  vaccinationReminderData: IVaccinationReminderResponseData[] | null;
  isEmrComplete?: boolean;
  vaccinationOverdue: IVaccinationOCRResponseData | null;
  reminders: IRemindersResponseData | null;
  exotelInformation: IExotelInformationData | null;
}

export interface ICustomerProfileInitialState extends IRouteReducerSliceStateInit {
  state: ICustomerProfileState;
}

const initialState: ICustomerProfileInitialState = {
  state: {
    customerProfileView: "DEFAULT",
    missingDetailsRedirect: false,
    customerSearchResults: [],
    customerId: null,
    petId: null,
    customerMembershipStatus: false,
    customerPrimaryAddress: null,
    userProfile: null,
    customerAddresses: [],
    customerDetails: null,
    customerPets: [],
    petDetails: null,
    headers: VMSConstants.array.customerProfileHeaderTabs,
    searchCustomerQuery: "",
    searchCustomerToggleCode: "0",
    customerEMRDetails: [],
    customerLABDetails: [],
    invoice: [],
    clinicManagersMobile: null,
    bookingCountList: null,
    appointmentFilter: null,
    customerMembershipDetails: null,
    vaccinationData: [],
    vaccinationInputData: [],
    vaccinationSummaryData: [],
    isEmrComplete: false,
    vaccinationReminderData: [],
    vaccinationOverdue: {},
    reminders: null,
    exotelInformation: null,
  },
};

const customerProfileSlice = createSlice({
  name: "customer_profile_slice",
  initialState: initialState,
  reducers: {
    RESET_CUSTOMER_PROFILE: (_state) => {
      return initialState;
    },
    setCustomerProfileView: (state, action: PayloadAction<CustomerProfileViewType>) => {
      state.state.customerProfileView = action.payload;
    },
    setMissingDetailsRedirect: (state, action: PayloadAction<boolean>) => {
      state.state.missingDetailsRedirect = action.payload;
    },
    setCustomerSearchResults: (state, action: PayloadAction<{ searchResults: ISearchCustomerResponseData[] }>) => {
      state.state.customerSearchResults = action.payload.searchResults;
    },
    setCustomerMembershipStatus: (state, action: PayloadAction<{ status: boolean }>) => {
      state.state.customerMembershipStatus = action.payload.status;
    },
    setIsEmrComplete: (state, action: PayloadAction<{ status: boolean }>) => {
      state.state.isEmrComplete = action.payload.status;
    },
    setCustomerMembershipDetails: (state, action: PayloadAction<{ membershipDetails: string }>) => {
      state.state.customerMembershipDetails = action.payload.membershipDetails;
    },
    setCustomerId: (state, action: PayloadAction<{ customerId: string }>) => {
      state.state.customerId = action.payload.customerId;
    },
    setCustomerAndPetId: (state, action: PayloadAction<{ customerId: string; petId: string }>) => {
      state.state.customerId = action.payload.customerId;
      state.state.petId = action.payload.petId;
    },
    setPetId: (state, action: PayloadAction<{ petId: string }>) => {
      state.state.petId = action.payload.petId;
    },
    setCustomerDetails: (state, action: PayloadAction<{ customerDetails: ICustomerDetailsCustomerProfile }>) => {
      state.state.customerDetails = action.payload.customerDetails;
    },
    setCustomerPrimaryAddress: (state, action: PayloadAction<ICustomerAddress>) => {
      state.state.customerPrimaryAddress = action.payload;
    },
    setCustomerAddresses: (
      state,
      action: PayloadAction<{
        addresses: ICustomerAddress[];
      }>,
    ) => {
      state.state.customerAddresses = action.payload.addresses;
    },

    setCustomerPets: (state, action: PayloadAction<{ pets: ICustomerPetListPet[] }>) => {
      state.state.customerPets = action.payload.pets;
    },

    setPetDetails: (state, action: PayloadAction<{ petDetails: ICustomerPetListPet }>) => {
      state.state.petDetails = action.payload.petDetails;
    },
    setCustomerUserProfile: (state, action: PayloadAction<{ data: ICustomerUserProfileResponseData }>) => {
      state.state.userProfile = action.payload.data;
    },

    setCustomerEMRDetails: (state, action: PayloadAction<{ data: IEMRListResponseData[] }>) => {
      state.state.customerEMRDetails = action.payload.data;
    },
    setCustomerLABTestDetails: (state, action: PayloadAction<{ data: ILABListResponseData[] }>) => {
      state.state.customerLABDetails = action.payload.data;
    },
    setCustomerSearchQuery: (state, action: PayloadAction<string>) => {
      state.state.searchCustomerQuery = action.payload;
    },

    setCustomerSearchQueryToggle: (state, action: PayloadAction<"0" | "1">) => {
      state.state.searchCustomerToggleCode = action.payload;
    },
    setCustomerDetailsTab: (state, action: PayloadAction<{ tabURL: string; state: { isActive?: boolean } }>) => {
      const tabs = state.state.headers ?? [];
      const updatedTabs = tabs.map((tab: any) => {
        return tab?.url === action.payload.tabURL ? { ...tab, ...state } : { ...tab, isActive: false };
      });
      state.state.headers = updatedTabs;
    },
    setInvoice: (state, action: PayloadAction<{ data: IIvoiceListResponseData[] }>) => {
      state.state.invoice = action.payload.data;
    },
    setClinicManagersMobile: (state, action: PayloadAction<{ data: ICustomerClinicmanagerPhoneResponseData }>) => {
      state.state.clinicManagersMobile = action.payload.data;
    },
    setBookingCountList: (state, action: PayloadAction<{ data: IBookingCountListResponseData }>) => {
      state.state.bookingCountList = action.payload.data;
    },
    setAppointmentFilter: (state, action: PayloadAction<AppointmentFilterType>) => {
      state.state.appointmentFilter = action.payload;
    },
    setVaccinationData: (state, action: PayloadAction<IVaccinationHistoryResponseData[]>) => {
      state.state.vaccinationData = action.payload;
    },
    setVaccinationInputData: (state, action: PayloadAction<IVaccinationInputResponseData[]>) => {
      state.state.vaccinationInputData = action.payload;
    },
    setVaccinationSummaryData: (state, action: PayloadAction<IVaccinationSummaryResponseData[]>) => {
      state.state.vaccinationSummaryData = action.payload;
    },
    setVaccinationReminderData: (state, action: PayloadAction<IVaccinationReminderResponseData[]>) => {
      state.state.vaccinationReminderData = action.payload;
    },
    setVaccinationOverdueData: (state, action: PayloadAction<IVaccinationOCRResponseData>) => {
      state.state.vaccinationOverdue = action.payload;
    },
    setReminders: (state, action: PayloadAction<IRemindersResponseData>) => {
      state.state.reminders = action.payload;
    },
    setExotelInformation: (state, action: PayloadAction<IExotelInformationData>) => {
      state.state.exotelInformation = action.payload;
    },
    clearExotelInformation: (state) => {
      state.state.exotelInformation = null;
    },
  },
});

export const {
  RESET_CUSTOMER_PROFILE,
  setCustomerDetailsTab,
  setCustomerEMRDetails,  
  setCustomerSearchResults,
  setCustomerLABTestDetails,
  setCustomerSearchQuery,
  setCustomerSearchQueryToggle,
  setCustomerUserProfile,
  setMissingDetailsRedirect,
  setCustomerAddresses,
  setCustomerDetails,
  setCustomerAndPetId,
  setCustomerId,
  setCustomerPets,
  setPetDetails,
  setPetId,
  setCustomerMembershipStatus,
  setCustomerMembershipDetails,
  setInvoice,
  setClinicManagersMobile,
  setBookingCountList,
  setAppointmentFilter,
  setCustomerPrimaryAddress,
  setCustomerProfileView,
  setVaccinationData,
  setVaccinationInputData,
  setVaccinationSummaryData,
  setIsEmrComplete,
  setVaccinationReminderData,
  setVaccinationOverdueData,
  setReminders,
  setExotelInformation,
  clearExotelInformation,
} = customerProfileSlice.actions;
export default customerProfileSlice.reducer;
